import { useEffect } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { useSharedSocket } from '@/features/calls/context/CallContext';

/**
 * When an admin force-offlines the current professional, the server emits
 * `professional:forced-offline` to their user room. Show a toast and refresh the
 * profile query so the availability toggle reflects the server-forced offline
 * state. Unlike suspension, we do NOT log the user out.
 */
export function useForcedOfflineNotice(): void {
  const socket = useSharedSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return;

    const onForced = (data: { reason?: string }): void => {
      toast.warning('Un amministratore ti ha messo offline.', {
        description: data?.reason ? `Motivo: ${data.reason}` : undefined,
      });
      void queryClient.invalidateQueries({ queryKey: ['professional-profile', 'me'] });
    };
    const onCleared = (): void => {
      toast.success('Puoi tornare online.');
      void queryClient.invalidateQueries({ queryKey: ['professional-profile', 'me'] });
    };

    socket.on('professional:forced-offline', onForced);
    socket.on('professional:forced-offline-cleared', onCleared);
    return () => {
      socket.off('professional:forced-offline', onForced);
      socket.off('professional:forced-offline-cleared', onCleared);
    };
  }, [socket, queryClient]);
}
